/**
 * DexScene (key 'Dex') — the warm living-dex album. A gentle grid across the
 * national dex (Gen 1–3, #001–#386): species you have SEEN show as soft silhouettes;
 * species you have CAUGHT bloom into full color. Tap a tile to open a detail card with
 * its types, base stats, flavor, and — when there is any — a quiet gateway into the
 * lore Codex.
 *
 * Deliberately NOT a completion checklist: the header reads as a warm album ("you’ve
 * met N friends so far"), never "X/386 — keep grinding". Touch-first, responsive, calm.
 * All truth (seen/caught) comes from the GameSession; the renderer owns no rules.
 */
import Phaser from 'phaser';
import type { SpeciesData, PokemonType, CodexEntry } from '@/shared';
import { SEED_CODEX } from '@/lore';
import type { GameSession } from '@/session';
import { COLORS, CSS, FONT, SPACING } from '../ui/theme.js';
import { Panel } from '../ui/Panel.js';
import { Button } from '../ui/Button.js';
import { ScrollList } from '../ui/ScrollList.js';
import { FocusRing } from '../ui/FocusRing.js';
import { moveGrid, type GridDir } from '../ui/gridNav.js';
import { domTextFieldFocused } from '../ui/keyboardGate.js';
import { Layout } from '../layout/index.js';
import type { SpriteResolver } from '../sprites/index.js';
import { SpriteResolver as SpriteResolverImpl } from '../sprites/index.js';
import { fadeIn, goTo, isReducedMotion } from '../flow.js';
import { REGISTRY_SPRITES } from '../PreloadScene.js';
import { getSession } from '../session.js';
import {
  typePill,
  showSpecies,
  showSpeciesAnimated,
  calmBackdrop,
  redrawBackdrop,
} from './parts.js';
import { buildBadgeCase, type BadgeCaseResult } from './badgeCase.js';
import { questView } from '../QuestLogScene.js';

/** Gen 1–3 national-dex range (the MVP slice). */
const DEX_MAX = 386;

const STAT_LABEL: Record<string, string> = {
  hp: 'HP',
  atk: 'Atk',
  def: 'Def',
  spatk: 'SpA',
  spdef: 'SpD',
  spe: 'Spe',
};
const STAT_ORDER = ['hp', 'atk', 'def', 'spatk', 'spdef', 'spe'] as const;

interface DexRow {
  readonly dexNo: number;
  readonly species: SpeciesData | undefined;
  readonly seen: boolean;
  readonly caught: boolean;
}

export class DexScene extends Phaser.Scene {
  private layout!: Layout;
  private session!: GameSession;
  private resolver!: SpriteResolver;
  private reduced = false;

  private backdrop!: Phaser.GameObjects.Graphics;
  private heading?: Phaser.GameObjects.Text;
  private subheading?: Phaser.GameObjects.Text;
  private backButton?: Button;

  // ── tabs: the living-dex grid + the Badge Case trophy shelf ──────────────────
  private tab: 'dex' | 'badges' = 'dex';
  private tabButtons: { tab: 'dex' | 'badges'; button: Button }[] = [];
  private badgeCase?: BadgeCaseResult;
  private badgeCursor = -1;

  private list?: ScrollList;
  private detail?: Phaser.GameObjects.Container;

  // ── keyboard grid nav ──────────────────────────────────────────────────────
  /** The keyboard cursor index over the dex rows; -1 until the player first uses a key. */
  private cursor = -1;
  private gridCols = 1;
  private tileSize = 84;
  /** Live grid viewport height, for keep-cursor-visible auto-scroll. */
  private listViewH = 0;
  /** Each tile's container (aligned to the dex row index), for ring positioning. */
  private cells: Phaser.GameObjects.Container[] = [];
  private focusRing?: FocusRing;
  private keyHandler?: (e: KeyboardEvent) => void;

  constructor() {
    super('Dex');
  }

  create(): void {
    // Phaser REUSES this scene instance, so create() re-runs on every RE-ENTRY (Town →
    // Dex → Town → Dex). The list/detail/chrome handles survive between visits — reset
    // them so a re-entry never touches a destroyed object before its rebuild.
    this.resetState();

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.session = getSession(this);
    this.resolver =
      (this.registry.get(REGISTRY_SPRITES) as SpriteResolver | undefined) ??
      new SpriteResolverImpl(this);
    this.reduced = isReducedMotion();

    this.backdrop = calmBackdrop(this, this.layout);

    this.heading = this.add
      .text(0, 0, 'Living Dex', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.subheading = this.add
      .text(0, 0, this.warmSummary(), {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    this.backButton = new Button(this, 0, 0, {
      label: '‹ Town',
      variant: 'ghost',
      width: 110,
      reducedMotion: this.reduced,
      onClick: () => goTo(this, 'Town'),
    });

    for (const tab of ['dex', 'badges'] as const) {
      const button = new Button(this, 0, 0, {
        label: tab === 'dex' ? 'Dex' : 'Badge Case',
        variant: 'ghost',
        width: 140,
        reducedMotion: this.reduced,
        onClick: () => this.switchTab(tab),
      });
      this.tabButtons.push({ tab, button });
    }

    this.focusRing = new FocusRing(this, { depth: 90 });

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.setupKeyboard();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    fadeIn(this);
  }

  /**
   * Full keyboard nav: arrows move the highlight across the tile grid (auto-scrolling the
   * list to keep the cursor visible), Enter opens the focused tile's detail (when it's a
   * seen/caught friend), Esc closes an open detail else returns to Town. Ignored while a
   * DOM text field is focused. Cleaned up on SHUTDOWN.
   */
  private setupKeyboard(): void {
    this.keyHandler = (e: KeyboardEvent): void => {
      if (typeof document !== 'undefined' && domTextFieldFocused(document.activeElement)) return;
      // With a detail card open, Esc closes it; other keys are swallowed underneath.
      if (this.detail) {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeDetail();
        }
        return;
      }
      // Tab / bracket keys switch tabs regardless of cursor state.
      if (e.key === 'Tab' || e.key === '[' || e.key === ']') {
        e.preventDefault();
        this.switchTab(this.tab === 'dex' ? 'badges' : 'dex');
        return;
      }
      const dirs: Record<string, GridDir> = {
        ArrowUp: 'up',
        ArrowDown: 'down',
        ArrowLeft: 'left',
        ArrowRight: 'right',
        w: 'up',
        s: 'down',
        a: 'left',
        d: 'right',
      };
      const dir = dirs[e.key];
      if (this.tab === 'badges') {
        // The Badge Case shelf: arrows walk the 8 slots; Enter/Space is a no-op (a trophy
        // shelf has nothing to open), Esc heads back to Town.
        if (dir) {
          e.preventDefault();
          const count = this.badgeCase?.cells.length ?? 0;
          const cols = this.badgeCase?.cols ?? 1;
          const start = this.badgeCursor < 0 ? 0 : moveGrid(this.badgeCursor, dir, count, cols);
          this.setBadgeCursor(start);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          goTo(this, 'Town');
        }
        return;
      }
      if (dir) {
        e.preventDefault();
        const count = this.cells.length;
        const start = this.cursor < 0 ? 0 : moveGrid(this.cursor, dir, count, this.gridCols);
        this.setCursor(start);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (this.cursor >= 0) {
          const row = this.rows()[this.cursor];
          if (row && (row.seen || row.caught)) this.openDetail(row);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        goTo(this, 'Town');
      }
    };
    if (typeof window !== 'undefined') window.addEventListener('keydown', this.keyHandler);
  }

  /** Seat the keyboard cursor on a tile: auto-scroll it into view + draw the focus ring. */
  private setCursor(index: number): void {
    const count = this.cells.length;
    if (count === 0) return;
    this.cursor = Math.max(0, Math.min(count - 1, index));
    const cell = this.cells[this.cursor];
    if (!cell || !this.list) return;
    // Keep the focused tile inside the scrolling viewport before drawing the ring.
    const top = cell.y - this.tileSize / 2;
    const bottom = cell.y + this.tileSize / 2;
    const off = this.list.scrollOffset;
    const pad = SPACING.sm;
    if (top < off) this.list.scrollTo(top - pad);
    else if (bottom > off + this.listViewH) this.list.scrollTo(bottom - this.listViewH + pad);
    const m = cell.getWorldTransformMatrix();
    this.focusRing?.show(m.tx, m.ty, this.tileSize, this.tileSize, 12);
    this.focusRing?.bringToTop();
  }

  /**
   * Drop every reference a prior visit's create() populated so re-entry starts clean.
   * Display objects were torn down by the previous SHUTDOWN (teardown()); this clears the
   * handles so nothing stacks. Safe on a first entry (everything already empty).
   * (`backdrop` is non-optional and reassigned in create() before use, so it stays.)
   */
  private resetState(): void {
    this.heading = undefined;
    this.subheading = undefined;
    this.backButton = undefined;
    this.tabButtons = [];
    this.tab = 'dex';
    this.badgeCase = undefined;
    this.badgeCursor = -1;
    this.list = undefined;
    this.detail = undefined;
    this.cells = [];
    this.cursor = -1;
    this.focusRing = undefined;
    this.keyHandler = undefined;
  }

  /**
   * SHUTDOWN handler — Phaser reuses this scene instance, so leave nothing behind: kill
   * tweens, destroy the backdrop/chrome + the grid list + any open detail overlay
   * (containers cascade to children), and drop the references.
   */
  private teardown(): void {
    this.tweens.killAll();
    if (this.keyHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    this.keyHandler = undefined;
    this.focusRing?.destroy();
    this.focusRing = undefined;
    this.cells = [];
    this.list?.destroy();
    this.detail?.destroy();
    this.badgeCase?.container.destroy();
    this.badgeCase = undefined;
    for (const { button } of this.tabButtons) button.destroy();
    this.tabButtons = [];
    this.backdrop?.destroy();
    this.heading?.destroy();
    this.subheading?.destroy();
    this.backButton?.destroy();
    this.heading = undefined;
    this.subheading = undefined;
    this.backButton = undefined;
    this.list = undefined;
    this.detail = undefined;
  }

  /** A warm, pressure-free album line — friends met, never an X/Y checklist. */
  private warmSummary(): string {
    const caught = this.rows().filter((r) => r.caught).length;
    const seen = this.rows().filter((r) => r.seen).length;
    if (caught === 0 && seen === 0) return 'your album is waiting — every friend gets a page';
    if (caught === 0) return `${seen} friend${seen === 1 ? '' : 's'} glimpsed so far`;
    return `${caught} friend${caught === 1 ? '' : 's'} in the album · ${seen} met along the way`;
  }

  /** All dex rows (full Gen 1–3 range; unknown numbers simply read as "unseen"). */
  private cachedRows?: DexRow[];
  private rows(): DexRow[] {
    if (this.cachedRows) return this.cachedRows;
    const index = this.session.bundle.index;
    const out: DexRow[] = [];
    for (let n = 1; n <= DEX_MAX; n++) {
      const species = index.speciesByDexNo(n);
      const id = species?.id;
      const seen = id ? this.session.hasSeen(id) : false;
      const caught = id ? this.session.hasCaught(id) : false;
      out.push({ dexNo: n, species, seen, caught });
    }
    this.cachedRows = out;
    return out;
  }

  // ── grid ────────────────────────────────────────────────────────────────────────

  private buildGrid(): void {
    this.list?.destroy();
    const safe = this.layout.safe;
    const top = this.contentTop;
    const listW = Math.min(safe.width - SPACING.lg * 2, 920);
    const listH = Math.max(180, safe.y + safe.height - top - SPACING.lg);
    const cx = this.layout.centerX;

    const list = new ScrollList(this, cx, top + listH / 2, { width: listW, height: listH });
    this.list = list;
    this.listViewH = listH;

    const tile = 84;
    const gap = SPACING.sm;
    const cols = Math.max(2, Math.floor((listW + gap) / (tile + gap)));
    const usedW = cols * tile + (cols - 1) * gap;
    const startX = -usedW / 2 + tile / 2;
    this.tileSize = tile;
    this.gridCols = cols;
    this.cells = [];

    const rows = this.rows();
    // ScrollList maps content-local y=0 to the viewport TOP, so lay tiles out from the
    // top down (NOT from -listH/2 as a centered layout would). The first dex row then
    // sits at the top on open and the last is reachable by scrolling.
    rows.forEach((row, i) => {
      const col = i % cols;
      const rowIdx = Math.floor(i / cols);
      const x = startX + col * (tile + gap);
      const y = SPACING.sm + tile / 2 + rowIdx * (tile + gap);
      const cell = this.tile(row, tile);
      cell.setPosition(x, y);
      list.content.add(cell);
      this.cells.push(cell);
    });

    const totalRows = Math.ceil(rows.length / cols);
    // Match the real laid-out height: SPACING.sm top pad + the rows' full extent.
    list.setContentHeight(SPACING.sm + totalRows * (tile + gap) + SPACING.md);
    list.scrollTo(0);
  }

  private tile(row: DexRow, size: number): Phaser.GameObjects.Container {
    const c = this.add.container(0, 0);
    const fill = row.caught ? COLORS.surface : COLORS.surfaceAlt;
    const panel = new Panel(this, 0, 0, { width: size, height: size, fill, radius: 12 });
    c.add(panel);

    const num = this.add
      .text(
        -size / 2 + SPACING.xs + 2,
        -size / 2 + SPACING.xs,
        `#${String(row.dexNo).padStart(3, '0')}`,
        {
          fontFamily: FONT.mono,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textFaint,
        },
      )
      .setOrigin(0, 0);
    c.add(num);

    if (row.species && (row.seen || row.caught)) {
      const sprite = this.add.image(0, 2, '__MISSING').setVisible(false);
      c.add(sprite);
      // Caught -> full color; seen-but-not-caught -> soft silhouette.
      void showSpecies(this, this.resolver, row.species.dexNo, sprite, size - 26, !row.caught);
    } else {
      // Never-seen: a quiet question mark, never a scolding blank.
      const q = this.add
        .text(0, 2, '?', {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xl}px`,
          color: CSS.textFaint,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      c.add(q);
    }

    if (row.caught) {
      // A tiny warm dot marks a friend who has joined the album.
      const dot = this.add.graphics();
      dot.fillStyle(COLORS.accent, 1);
      dot.fillCircle(size / 2 - SPACING.sm, -size / 2 + SPACING.sm, 3);
      c.add(dot);
    }

    if (row.seen || row.caught) {
      panel.setInteractive(
        new Phaser.Geom.Rectangle(-size / 2, -size / 2, size, size),
        Phaser.Geom.Rectangle.Contains,
      );
      panel.input!.cursor = 'pointer';
      // Open on TAP (POINTER_UP); a drag-scroll that began on this tile no-ops here so
      // touch-dragging the dex grid scrolls instead of opening a detail card.
      panel.on(Phaser.Input.Events.POINTER_UP, () => {
        if (!this.list?.wasDragging()) this.openDetail(row);
      });
    }
    return c;
  }

  /**
   * The y where the grid begins (under the header chrome). Computed by relayout() —
   * which stacks the header differently on a phone vs desktop — so the grid always
   * starts cleanly below whatever header height the current viewport produced.
   */
  private headerBottom = 0;
  private get contentTop(): number {
    return this.headerBottom;
  }

  // ── detail + lore gateway ────────────────────────────────────────────────────────

  private openDetail(row: DexRow): void {
    if (!row.species) return;
    this.closeDetail();
    const species = row.species;
    const safe = this.layout.safe;
    const w = Math.min(420, safe.width - SPACING.md * 2);
    const h = Math.min(safe.height - SPACING.lg * 2, 520);
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;

    const overlay = this.add.container(0, 0);
    overlay.setDepth(100);

    const scrim = this.add.graphics();
    scrim.fillStyle(COLORS.scrim, 0.6);
    scrim.fillRect(0, 0, this.layout.width, this.layout.height);
    scrim.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, this.layout.width, this.layout.height),
      Phaser.Geom.Rectangle.Contains,
    );
    scrim.on(Phaser.Input.Events.POINTER_DOWN, () => this.closeDetail());
    overlay.add(scrim);

    const panel = new Panel(this, cx, cy, {
      width: w,
      height: h,
      fill: COLORS.surface,
      shadow: true,
    });
    panel.setInteractive(
      new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    overlay.add(panel);

    const card = this.add.container(cx, cy);
    overlay.add(card);

    const innerW = w - SPACING.lg * 2;
    const left = -w / 2 + SPACING.lg;
    let y = -h / 2 + SPACING.lg;

    // The detail view animates: a Sprite playing the 3D render's gentle looping idle
    // (only the selected/detail sprite animates — the grid stays static for smooth
    // scrolling). Falls back to the Gen5 static sprite when no render exists.
    const sprite = this.add.sprite(left + 40, y + 36, '__MISSING').setVisible(false);
    void showSpeciesAnimated(
      this,
      this.resolver,
      species.dexNo,
      sprite,
      84,
      !row.caught,
      this.reduced,
    );
    card.add(sprite);

    const title = this.add.text(left + 92, y, species.name, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.lg}px`,
      color: CSS.text,
      fontStyle: 'bold',
    });
    const dexLabel = this.add.text(
      left + 92,
      y + FONT.size.lg + 4,
      `#${String(species.dexNo).padStart(3, '0')} · ${row.caught ? 'in your album' : 'glimpsed'}`,
      { fontFamily: FONT.family, fontSize: `${FONT.size.sm}px`, color: CSS.textMuted },
    );
    card.add([title, dexLabel]);

    let tx = left + 92;
    for (const t of species.types) {
      const pill = typePill(this, 0, y + FONT.size.lg + FONT.size.sm + 12, t as PokemonType);
      pill.x = tx + pill.width / 2;
      tx += pill.width + SPACING.xs;
      card.add(pill);
    }

    y += 92;

    // Base stats — shown for caught; gently veiled (just a hint) when only seen.
    if (row.caught) {
      const heading = this.add.text(left, y, 'Base stats', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.accent,
        fontStyle: 'bold',
      });
      card.add(heading);
      y += FONT.size.sm + SPACING.sm;
      const maxBase = Math.max(1, ...STAT_ORDER.map((k) => species.baseStats[k]));
      for (const key of STAT_ORDER) {
        const label = this.add.text(left, y, STAT_LABEL[key], {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textMuted,
        });
        const barW = innerW * 0.5;
        const g = this.add.graphics();
        g.fillStyle(COLORS.surfaceAlt, 1);
        g.fillRoundedRect(left + 44, y + 1, barW, 6, 3);
        g.fillStyle(COLORS.accent, 0.9);
        g.fillRoundedRect(
          left + 44,
          y + 1,
          Math.max(2, barW * (species.baseStats[key] / maxBase)),
          6,
          3,
        );
        const value = this.add
          .text(left + 44 + barW + SPACING.sm, y, `${species.baseStats[key]}`, {
            fontFamily: FONT.mono,
            fontSize: `${FONT.size.xs}px`,
            color: CSS.text,
          })
          .setOrigin(0, 0);
        card.add([label, g, value]);
        y += FONT.size.xs + SPACING.sm - 1;
      }
    } else {
      const veil = this.add.text(
        left,
        y,
        'You’ve only crossed paths so far. Walk with this one a while and its page will fill in.',
        {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.textMuted,
          wordWrap: { width: innerW },
          lineSpacing: 3,
        },
      );
      card.add(veil);
      // (No further `y` flow below — the lore hint + action buttons anchor to actionY.)
    }

    // Lore gateway — only when there is related codex flavor to wander into.
    const related = this.relatedLore(species);
    const actionY = h / 2 - SPACING.lg - 12;
    const close = new Button(this, 0, actionY, {
      label: 'Close',
      variant: 'ghost',
      width: 120,
      reducedMotion: this.reduced,
      onClick: () => this.closeDetail(),
    });
    card.add(close);

    if (related.length > 0) {
      // Seat the lore hint just ABOVE the action button row (measured up from it), not at
      // the flowing `y` — on a narrow phone the seen-veil/stats can push `y` down onto the
      // bottom-pinned buttons, so anchoring to the buttons keeps the hint clear of them.
      const loreHint = this.add.text(left, 0, '✦ There’s a tale about this one…', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.warm,
        wordWrap: { width: innerW },
      });
      const btnHalf = (close.buttonHeight ?? 48) / 2;
      loreHint.setY(actionY - btnHalf - SPACING.md - loreHint.height);
      card.add(loreHint);

      const open = new Button(this, 0, actionY, {
        label: 'Read the lore',
        variant: 'warm',
        width: 160,
        reducedMotion: this.reduced,
        onClick: () => this.openLore(species.id),
      });
      close.x = -SPACING.sm - 70;
      open.x = SPACING.sm + 90;
      card.add(open);
    }

    this.detail = overlay;
    if (!this.reduced) {
      overlay.setAlpha(0);
      this.tweens.add({ targets: overlay, alpha: 1, duration: 140, ease: 'Sine.easeOut' });
    }
  }

  /** Authored codex entries that mention this species (a quiet, optional gateway). */
  private relatedLore(species: SpeciesData): readonly CodexEntry[] {
    return SEED_CODEX.filter((e) => e.relatedSpeciesIds.includes(species.id));
  }

  /** Hand off to the Codex scene, focused on this species' thread. */
  private openLore(speciesId: string): void {
    this.closeDetail();
    goTo(this, 'Codex', { data: { focusSpeciesId: speciesId } });
  }

  private closeDetail(): void {
    this.detail?.destroy();
    this.detail = undefined;
  }

  // ── tabs ────────────────────────────────────────────────────────────────────────

  private switchTab(tab: 'dex' | 'badges'): void {
    if (tab === this.tab) return;
    this.tab = tab;
    this.cursor = -1;
    this.badgeCursor = -1;
    this.closeDetail();
    this.focusRing?.hide();
    this.highlightTabs();
    this.subheading?.setText(this.tabSummary());
    this.renderBody();
  }

  private highlightTabs(): void {
    for (const { tab, button } of this.tabButtons) button.setAlpha(tab === this.tab ? 1 : 0.55);
  }

  private tabSummary(): string {
    if (this.tab === 'badges') {
      const n = questView(this.session).questState.badges.length;
      return n === 0
        ? 'trophies from the Badge Circuit gather here'
        : `${n} badge${n === 1 ? '' : 's'} on the shelf`;
    }
    return this.warmSummary();
  }

  /** Build whichever tab is active into the content area below the header. */
  private renderBody(): void {
    this.list?.destroy();
    this.list = undefined;
    this.badgeCase?.container.destroy();
    this.badgeCase = undefined;
    this.cells = [];
    if (this.tab === 'dex') {
      this.buildGrid();
      if (this.cursor >= 0) this.setCursor(this.cursor);
    } else {
      this.buildBadges();
      if (this.badgeCursor >= 0) this.setBadgeCursor(this.badgeCursor);
    }
  }

  private buildBadges(): void {
    const safe = this.layout.safe;
    const top = this.contentTop;
    const width = Math.min(safe.width - SPACING.lg * 2, 720);
    const height = Math.max(180, safe.y + safe.height - top - SPACING.lg);
    this.badgeCase = buildBadgeCase(this, {
      cx: this.layout.centerX,
      top,
      width,
      height,
      badges: questView(this.session).questState.badges,
    });
  }

  /** Seat the keyboard focus ring on a badge slot. */
  private setBadgeCursor(index: number): void {
    const cells = this.badgeCase?.cells ?? [];
    if (cells.length === 0) {
      this.focusRing?.hide();
      return;
    }
    this.badgeCursor = Math.max(0, Math.min(cells.length - 1, index));
    const cell = cells[this.badgeCursor];
    if (!cell) return;
    const m = cell.container.getWorldTransformMatrix();
    this.focusRing?.show(m.tx, m.ty, cell.width, cell.height);
    this.focusRing?.bringToTop();
  }

  // ── layout ──────────────────────────────────────────────────────────────────────

  private relayout(): void {
    redrawBackdrop(this.backdrop, this.layout);
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const top = safe.y + SPACING.md;

    this.subheading?.setText(this.tabSummary());

    const backW = this.backButton?.buttonWidth ?? 110;
    const backH = this.backButton?.buttonHeight ?? 48;
    const headingH = this.heading?.height ?? 24;
    const subH = this.subheading?.height ?? 18;
    const subW = this.subheading?.width ?? 0;
    const backCx = safe.x + SPACING.md + backW / 2;

    // The centered warm-summary subtitle is wide and on a narrow phone it collides with
    // the left back button (both horizontally and vertically). So on a COMPACT viewport
    // we STACK the header — back button alone on row 1, then the heading + subtitle
    // centered below it — guaranteeing no overlap on ~390px. A roomy desktop keeps the
    // original two-line centered header with the back button tucked top-left beside it.
    const subClearsBack = cx - subW / 2 >= backCx + backW / 2 + SPACING.sm;
    const stacked = this.layout.isCompact || !subClearsBack;

    if (stacked) {
      const backY = top + backH / 2;
      this.backButton?.setPosition(backCx, backY);
      const headingY = backY + backH / 2 + SPACING.sm + headingH / 2;
      this.heading?.setPosition(cx, headingY);
      const subY = headingY + headingH / 2 + SPACING.xs + subH / 2;
      this.subheading?.setPosition(cx, subY);
      this.headerBottom = subY + subH / 2 + SPACING.md;
    } else {
      this.backButton?.setPosition(backCx, top + backH / 2);
      const headingY = top + headingH / 2;
      this.heading?.setPosition(cx, headingY);
      const subY = headingY + headingH / 2 + SPACING.xs + subH / 2;
      this.subheading?.setPosition(cx, subY);
      this.headerBottom = subY + subH / 2 + SPACING.md;
    }

    // A tab row (Dex · Badge Case) sits below the header text; the body starts under it.
    const tabH = this.tabButtons[0]?.button.buttonHeight ?? 48;
    const tabW = this.tabButtons[0]?.button.buttonWidth ?? 140;
    const tabY = this.headerBottom + tabH / 2;
    const gap = SPACING.sm;
    const totalTabsW = this.tabButtons.length * tabW + (this.tabButtons.length - 1) * gap;
    let tx = cx - totalTabsW / 2 + tabW / 2;
    for (const { button } of this.tabButtons) {
      button.setPosition(tx, tabY);
      tx += tabW + gap;
    }
    this.headerBottom = tabY + tabH / 2 + SPACING.md;
    this.highlightTabs();

    this.closeDetail();
    this.renderBody();
    // Re-seat the keyboard focus ring on the (rebuilt) focused tile after a relayout.
    if (this.tab === 'dex') {
      if (this.cursor >= 0) this.setCursor(this.cursor);
      else this.focusRing?.hide();
    } else if (this.badgeCursor >= 0) {
      this.setBadgeCursor(this.badgeCursor);
    } else {
      this.focusRing?.hide();
    }
  }
}
